import { type CategoryTreeCategory } from 'components/modal/categories/signals.ts';
import type { CategoryData, OfferCategoryData } from 'service/category/model.ts';
import { categoriesMap, memoizedCategories } from 'signals/category';

export type BazarCategoryTreeCategory = Omit<CategoryTreeCategory, 'searchToken' | 'children'> & {
	children: BazarCategoryTreeCategory[];
};
export type BazarCategoryTree = Record<number, BazarCategoryTreeCategory>;
/**
 * Creates a tree structure from an incomplete list of categories. Automatically finds parents on the way up and sums
 * the offer counts accordingly. Differs from {@link generateCategoryTree} by not requiring a full list of categories,
 * and summing the offer counts.
 */
export function makeCategoriesTree(categories: CategoryData[]): CategoryTreeCategory[] {
	const allCategoriesMap = memoizedCategories.categoriesMap;
	const categoryTree: Record<number, CategoryTreeCategory> = {};
	const processedCategories = new Set<number>();

	for (const category of categories) {
		const count = category.offersCount ?? 0;
		attachSelfToParent(category, allCategoriesMap, categoryTree, category.offersCount ?? 0, processedCategories);
		category.offersCount = count;
	}

	return Object.values(categoryTree);
}

/**
 * Recursive method
 * @param category
 * @param allCategoriesMap
 * @param bazarCategoryTree
 * @param offerCountIncrease
 * @param processedCategories
 */
function attachSelfToParent(
	category: CategoryData | CategoryTreeCategory | BazarCategoryTreeCategory,
	allCategoriesMap: Record<number, CategoryTreeCategory>,
	bazarCategoryTree: Record<number, BazarCategoryTreeCategory>,
	offerCountIncrease: number,
	processedCategories: Set<number>,
) {
	if (category.parentId) {
		const parentCategory = allCategoriesMap[category.parentId];

		if (!parentCategory) {
			throw new Error(`Category ${category.seoName} has unknown parent.`);
		} else {
			attachSelfToParent(
				parentCategory,
				allCategoriesMap,
				bazarCategoryTree,
				offerCountIncrease,
				processedCategories,
			);
			if (!processedCategories.has(category.id)) {
				processedCategories.add(category.id);
				category.offersCount = 0;
				(category as BazarCategoryTreeCategory).children = [];
				(category as BazarCategoryTreeCategory).parent = null;
				// @ts-expect-error it is okay to try to delete optional property. Delete it to save on network traffic
				delete category.searchToken;
			}
			if (
				(parentCategory as BazarCategoryTreeCategory).children.indexOf(
					category as BazarCategoryTreeCategory,
				) === -1
			) {
				(parentCategory as BazarCategoryTreeCategory).children.push(category as BazarCategoryTreeCategory);
			}
			parentCategory.offersCount = (parentCategory.offersCount ?? 0) + offerCountIncrease;
		}
	}
	// Top-level category, attach self to tree root it is not yet and convert it to BazarCategoryTreeCategory
	else {
		if (!bazarCategoryTree[category.id]) {
			// Mutate the category, so we can keep the reference from within the recursive calls
			(category as BazarCategoryTreeCategory).children = [];
			(category as BazarCategoryTreeCategory).parent = null;
			(category as BazarCategoryTreeCategory).offersCount = 0;
			bazarCategoryTree[category.id] = category as BazarCategoryTreeCategory;
			processedCategories.add(category.id);
		}
	}
}

export function offerCategoryDataToCategoryData(offerCategory: OfferCategoryData): CategoryData {
	const category = categoriesMap.peek()[offerCategory.id]!;

	return {
		id: category.id,
		name: category.name,
		seoName: category.seoName,
		offersCount: offerCategory.offersCount,
		parentId: category.parentId,
		level: category.level,
		path: category.path?.concat(), // Create a copy of the path if it exists
		seoKeywords: category.seoKeywords,
		title: category.title,
	};
}

export function getOffersCountMap(
	availableCategories: BazarCategoryTreeCategory[],
	categories: { id: number; offersCount?: number }[],
) {
	const cache = categories.reduce<Record<number, { id: number; offersCount?: number }>>((prev, currentValue) => {
		prev[currentValue.id] = currentValue;
		return prev;
	}, {});
	const offerCounts: Record<number, number> = {};

	for (const category of availableCategories) {
		offerCounts[category.id] = getCategoryCount(category, cache, offerCounts);
	}

	return offerCounts;
}

/**
 * Method that recursively counts the number of offers in a category and its children, summing it all together.
 * @param category
 * @param cache
 * @param offerCounts
 */
export function getCategoryCount(
	category: BazarCategoryTreeCategory,
	cache: Record<number, { offersCount?: number }>,
	offerCounts: Record<number, number>,
) {
	if (category.children.length) {
		const offerCount: number = category.children.reduce<number>((prev, currentValue) => {
			return prev + getCategoryCount(currentValue, cache, offerCounts);
		}, 0);
		return (offerCounts[category.id] = offerCount);
	} else {
		return (offerCounts[category.id] = cache[category.id]?.offersCount ?? 0);
	}
}

/**
 * Returns a map of categories that should be expanded in order to see the `selectedCategoryId` in category select.
 * @param selectedCategoryId
 */
export function getExpandedCategories(selectedCategoryId: number) {
	let currentCategory: CategoryTreeCategory | null | undefined = memoizedCategories.categoriesMap[selectedCategoryId];
	if (!currentCategory) return {};

	const expandedCategories: Record<number, boolean> = {};

	do {
		expandedCategories[currentCategory.id] = true;
		currentCategory = currentCategory.parent;
	} while (currentCategory);
	return expandedCategories;
}

/**
 * Recursive function. Checks if the category (node) is the parent category of the category with the id
 * `parentCategoryId`. If not, recursively checks the children of the category.
 * Intended usage is to call this method on the root category / root categories, to find the parent category of a
 * category. Used for a tree  structure that doe
 * @param node
 * @param parentCategoryId
 */
export const getCategoryParent = (
	node: BazarCategoryTreeCategory,
	parentCategoryId: number,
): BazarCategoryTreeCategory | undefined => {
	if (node.id === parentCategoryId) {
		return node;
	}
	if (node.children) {
		for (const child of node.children) {
			const found = getCategoryParent(child, parentCategoryId);
			if (found) {
				return found;
			}
		}
	}
	return undefined;
};

/**
 * Helper for analytics. Gets the amount of categories based on the all available categories and the current active
 * category. This is used for analytics and is meant to emulate the count of categories shown on search page.
 * On search page, the categories are being loaded as the user traverses. This currently loaded amount of categories gets
 * logged.
 * This function emulates the same behavior, but on bazar page, where all the categories are listed in an expandable
 * tree.
 * @param categoryTree
 * @param activeCategoryId
 */
export function getAnalyticsCategoryAmount(
	categoryTree: BazarCategoryTree,
	activeCategoryId: number | null | undefined,
) {
	const categoryArray = Object.values(categoryTree);
	if (!activeCategoryId) {
		return categoryArray.length;
	} else {
		const found = findAnalyticsCategory(activeCategoryId, categoryArray);
		if (!found) return categoryArray.length;
		else return found.children.length;
	}
}

/**
 * Recursively finds a category in a tree structure. If the found category has no children (is a leaf category) return
 * the parent category instead.
 * @param targetCategoryId
 * @param categories
 */
function findAnalyticsCategory(
	targetCategoryId: number,
	categories: BazarCategoryTreeCategory[],
): BazarCategoryTreeCategory | undefined {
	for (const category of categories) {
		if (category.id === targetCategoryId) {
			return category;
		}
		const found = findAnalyticsCategory(targetCategoryId, category.children);
		if (found) {
			if (found.children.length === 0) {
				return category;
			} else {
				return found;
			}
		}
	}
	return undefined;
}
